import { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { X, Download, Share } from 'lucide-react';

const DISMISS_KEY = 'verdictai_pwa_install_dismissed';

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (sessionStorage.getItem(DISMISS_KEY)) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS Safari doesn't fire beforeinstallprompt — show a manual banner
    if (isIOS()) {
      const t = setTimeout(() => setVisible(true), 2500);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      };
    }
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const install = useCallback(async () => {
    if (isIOS()) {
      setShowIOSHelp(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
    sessionStorage.setItem(DISMISS_KEY, '1');
  }, [deferredPrompt]);

  const dismiss = () => {
    setVisible(false);
    setShowIOSHelp(false);
    sessionStorage.setItem(DISMISS_KEY, '1');
  };

  if (!visible) return null;

  if (showIOSHelp) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-[60] md:left-auto md:max-w-sm bg-card border rounded-2xl shadow-2xl p-4" data-testid="pwa-ios-help">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <p className="text-sm font-semibold mb-2">Install VerdictIQ on iPhone</p>
            <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Tap the <Share className="inline h-3 w-3" /> Share button</li>
              <li>Scroll and tap “Add to Home Screen”</li>
              <li>Tap “Add” — done!</li>
            </ol>
          </div>
          <button onClick={dismiss} className="p-1 hover:bg-muted rounded" data-testid="pwa-close-ios">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[60] md:left-auto md:max-w-sm bg-card border rounded-2xl shadow-2xl p-4 flex items-center gap-3" data-testid="pwa-install-banner">
      <img src="/logo.png" alt="" className="h-12 w-12 object-contain shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">Install VerdictIQ</p>
        <p className="text-xs text-muted-foreground">Get the full app experience on your phone</p>
      </div>
      <Button size="sm" className="rounded-full h-9 px-4 text-xs shrink-0" onClick={install} data-testid="pwa-install-btn">
        <Download className="h-3.5 w-3.5 mr-1" /> Install
      </Button>
      <button onClick={dismiss} className="p-1 hover:bg-muted rounded shrink-0" data-testid="pwa-dismiss-btn">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
